import { CLIENT_FIELD_APPLIES_TO_ALL } from "@repo/shared/types";
import { useQueryClient } from "@tanstack/react-query";
import { Pencil, Trash2 } from "lucide-react";
import { useTransition } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useConfirm } from "@/hooks/use-confirm";
import { del, post } from "@/lib/api/_client";
import * as customFieldsApi from "@/lib/api/custom-fields";
import { useDeleteCheck } from "@/lib/hooks/use-delete-check";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./custom-fields-table.i18n";
import type { ClientTab, CustomField, CustomFieldType } from "./settings-types";

type CustomFieldsTableProps = {
  fields: CustomField[];
  clientTabs?: ClientTab[];
  onEdit: (field: CustomField) => void;
};

export function CustomFieldsTable({ fields, clientTabs = [], onEdit }: CustomFieldsTableProps) {
  const localize = useLocalize();
  const t = useT(T);
  const [isPending, startTransition] = useTransition();
  const { confirm, ConfirmationDialog } = useConfirm();
  const queryClient = useQueryClient();

  const invalidateFields = () => queryClient.invalidateQueries({ queryKey: ["customFields"] });

  const handleTabChange = async (fieldId: string, tabId: string | null, isStandard: boolean) => {
    startTransition(async () => {
      try {
        await post("/internal/custom-fields/assign-tab", { fieldId, tabId, isStandard });
      } catch (err: any) {
        await confirm({
          title: t.error,
          description: err.message || t.failedToAssignTab,
          confirmText: "OK",
          variant: "destructive",
          type: "alert",
        });
      }
      invalidateFields();
    });
  };

  const handleToggleVisibility = async (
    id: string,
    field: "visibleInList" | "visibleInProfile",
  ) => {
    startTransition(async () => {
      try {
        await post(`/internal/custom-fields/${id}/toggle-visibility`, { field });
      } catch (err: any) {
        await confirm({
          title: t.error,
          description: err.message || t.failedToToggleVisibility,
          confirmText: "OK",
          variant: "destructive",
          type: "alert",
        });
      }
      invalidateFields();
    });
  };

  const showDeleteError = (message: string) =>
    confirm({
      title: t.error,
      description: message,
      confirmText: "OK",
      variant: "destructive",
      type: "alert",
    });

  const confirmAndDelete = async (id: string) => {
    const field = fields.find((f) => f.id === id);
    const displayName = field ? localize(field.nameI18n) || field.name : "";
    const confirmed = await confirm({
      title: t.deleteCustomField,
      description: t.deleteConfirmation(displayName),
      confirmText: t.delete,
      cancelText: t.cancel,
      variant: "destructive",
    });

    if (!confirmed) return;

    startTransition(async () => {
      try {
        await del(`/internal/custom-fields/${id}`);
      } catch (err: any) {
        await showDeleteError(err.message || t.failedToDelete);
      }
      invalidateFields();
    });
  };

  // QL-168 — a field the server would refuse to delete shows why straight away;
  // the confirmation only opens when the delete is allowed.
  const { requestDelete, checkingId } = useDeleteCheck({
    check: customFieldsApi.checkDelete,
    onAllowed: (id) => void confirmAndDelete(id),
    onBlocked: (reason) => showDeleteError(reason || t.failedToDelete),
  });

  // Keyed by CustomFieldType so a new field type is a compile error here rather
  // than silently rendering its raw slug. Labels must match custom-field-dialog.
  const getFieldTypeLabel = (type: CustomFieldType) => {
    const labels: Record<CustomFieldType, string> = {
      text: t.typeText,
      textarea: t.typeTextarea,
      number: t.typeNumber,
      date: t.typeDate,
      datetime: t.typeDateTime,
      dropdown: t.typeDropdown,
      radio: t.typeRadio,
      multiselect: t.typeMultiselect,
      boolean: t.typeBoolean,
      user: t.typeUser,
    };
    return labels[type] || type;
  };

  const getClientTypeBadge = (clientType: string) => {
    switch (clientType) {
      case CLIENT_FIELD_APPLIES_TO_ALL:
        return <Badge variant="secondary">{t.allTypes}</Badge>;
      case "Person":
        return <Badge variant="outline">{t.person}</Badge>;
      case "Company":
        return <Badge variant="outline">{t.company}</Badge>;
      case "Group":
        return <Badge variant="outline">{t.group}</Badge>;
      default:
        // Not a value the platform recognises, so `fieldAppliesToClient` matches
        // no client at all. Show it raw rather than labelling it "All" -- a field
        // that applies to nobody must not look like a working all-types field.
        return <Badge variant="destructive">{clientType}</Badge>;
    }
  };

  // A type-specific field (anything but the all-types value) has no visibleInList
  // toggle — the client table only carries a column for a field every client has.
  const isTypeSpecificField = (field: CustomField) => {
    return field.clientType !== CLIENT_FIELD_APPLIES_TO_ALL;
  };

  if (fields.length === 0) {
    return <EmptyState>{t.noFieldsYet}</EmptyState>;
  }

  return (
    <>
      <Table variant="data">
        <TableHeader>
          <TableRow>
            <TableHead>{t.name}</TableHead>
            <TableHead>{t.type}</TableHead>
            <TableHead>{t.appliesTo}</TableHead>
            {clientTabs.length > 0 && <TableHead>{t.tab}</TableHead>}
            <TableHead>{t.visibleInList}</TableHead>
            <TableHead>{t.visibleInProfile}</TableHead>
            <TableHead className="text-right">{t.actions}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {fields.map((field) => (
            <TableRow key={field.id}>
              <TableCell className="font-medium">
                {localize(field.nameI18n) || field.name}
                {field.isStandard && (
                  <span className="ml-2 text-xs text-muted-foreground">{t.standard}</span>
                )}
              </TableCell>
              <TableCell>{getFieldTypeLabel(field.type)}</TableCell>
              <TableCell>{getClientTypeBadge(field.clientType)}</TableCell>
              {clientTabs.length > 0 && (
                <TableCell>
                  <Select
                    value={field.tabId || "none"}
                    onValueChange={(value) =>
                      handleTabChange(
                        field.id,
                        value === "none" ? null : value,
                        field.isStandard ?? false,
                      )
                    }
                    disabled={isPending}
                  >
                    <SelectTrigger className="w-[140px] h-8">
                      <SelectValue placeholder={t.defaultTab} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t.defaultTab}</SelectItem>
                      {clientTabs.map((tab) => (
                        <SelectItem key={tab.id} value={tab.id}>
                          {tab.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
              )}
              <TableCell>
                {field.id === "standard-name" ? (
                  <span className="text-xs text-muted-foreground">{t.alwaysVisible}</span>
                ) : isTypeSpecificField(field) ? (
                  <span className="text-xs text-muted-foreground">{t.notApplicable}</span>
                ) : (
                  <Switch
                    aria-label={`${t.visibleInList}: ${localize(field.nameI18n) || field.name}`}
                    checked={field.visibleInList}
                    onCheckedChange={() => handleToggleVisibility(field.id, "visibleInList")}
                    disabled={isPending}
                  />
                )}
              </TableCell>
              <TableCell>
                {field.id === "standard-name" ? (
                  <span className="text-xs text-muted-foreground">{t.alwaysVisible}</span>
                ) : (
                  <Switch
                    aria-label={`${t.visibleInProfile}: ${localize(field.nameI18n) || field.name}`}
                    checked={field.visibleInProfile}
                    onCheckedChange={() => handleToggleVisibility(field.id, "visibleInProfile")}
                    disabled={isPending}
                  />
                )}
              </TableCell>
              <TableCell className="text-right">
                {!field.isStandard && (
                  <div className="flex justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEdit(field)}
                      disabled={isPending}
                      tooltip={t.edit}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => requestDelete(field.id)}
                      loading={checkingId === field.id}
                      disabled={isPending}
                      tooltip={t.delete}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
                {field.isStandard && (
                  <span className="text-xs text-muted-foreground">{t.cannotEdit}</span>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <ConfirmationDialog />
    </>
  );
}
